import { SubscriptionApp } from '@focuslee/shared';
import { Stack } from '@mui/material';
import { Caption, Text } from '@telegram-apps/telegram-ui';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonIcon } from '../components/CommonIcon';
import { TELEGRAM_STAR_COLOR } from '../helpers/telegramStarColor';
import { getPremiumState, PremiumUser } from './premiumState';

export function PremiumHeader({
    user,
    renewalMissed,
    app,
    artwork,
}: {
    user: PremiumUser;
    renewalMissed: boolean;
    app: SubscriptionApp;
    artwork: ReactNode;
}) {
    const { t, i18n } = useTranslation('subscription');
    const state = getPremiumState(user);
    const date = state.expiresAt
        ? new Intl.DateTimeFormat(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' }).format(state.expiresAt * 1000)
        : null;

    const getStatusText = () => {
        if (state.isTrialActive) {
            const daysLeft = Math.max(0, Math.ceil(((state.expiresAt ?? 0) - Date.now() / 1000) / 86400));
            return t('trialActiveText', { daysLeft });
        }
        if (state.isManualPremium) {
            return t(date ? 'manualActiveText' : 'manualActiveSimple', { date });
        }
        if (user.subscription?.subscriptionCanceledAt && date) return t('subscriptionCanceledText', { date });
        if (state.isSubscriptionActive && date) return t('subscriptionActiveText', { date });
        if (state.isSubscriptionActive) return t('subscriptionActiveSimple');
        if (renewalMissed) return t('renewalMissingText');
        if (state.isAdmin) return t('subscribeDescription');
        return t(state.hasUsedTrial ? 'subscribeDescription' : 'trialDescription');
    };

    return (
        <Stack alignItems="center" spacing={1}>
            {artwork}
            <Text style={{ fontSize: 20, fontWeight: 800 }}>
                {t(state.hasPremium ? 'activeTitle' : 'subscriptionTitle', {
                    appName: app === SubscriptionApp.healthlee ? 'HealthLee' : 'FocusLee',
                })}
            </Text>
            {state.canSubscribe && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <CommonIcon icon="star" type="filled" size={16} color={TELEGRAM_STAR_COLOR} />
                    <Text style={{ fontSize: 18, fontWeight: 700 }}>
                        {state.product.amount} {t('pricePeriod')}
                    </Text>
                </Stack>
            )}
            <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 13 }}>{getStatusText()}</Caption>
        </Stack>
    );
}
